import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { KeyRound, Loader2, CheckCircle2, XCircle, Plus, Search, Download, Users, AlertCircle, Pencil, Check, X, ArrowLeft, Shield } from 'lucide-react';

const PROVIDERS = [
  { id: 'painelfoda', name: 'PainelFoda', tint: 'from-sky-500 to-indigo-600', hint: 'p.seupainel.top', example: 'https://p.seupainel.top' },
  { id: 'sigma', name: 'Sigma', tint: 'from-emerald-500 to-teal-600', hint: 'seupainel.top', example: 'https://seupainel.top' },
  { id: 'dominus', name: 'Dominus', tint: 'from-amber-500 to-orange-600', hint: 'seupainel.top', example: 'https://seupainel.com', token: true },
  { id: 'natv', name: 'NATV', tint: 'from-rose-500 to-pink-600', hint: 'revenda.pixbot.link', example: 'https://revenda.pixbot.link', token: true, fixedUrl: 'https://revenda.pixbot.link' },
];
const providerMeta = (id) => PROVIDERS.find((p) => p.id === id) || { name: 'Painel', tint: 'from-slate-500 to-slate-700' };

const errText = (e, fallback) => {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || x?.message || '').filter(Boolean).join('; ') || fallback;
  if (d && typeof d === 'object') return d.msg || d.message || fallback;
  return e?.message || fallback;
};

export default function SigmaLoginCard({ api, connections, onChanged }) {
  const [loading, setLoading] = useState(true);
  const [billOffer, setBillOffer] = useState(null);
  const [integrations, setIntegrations] = useState([]);
  const [activeId, setActiveId] = useState(null);

  // Conexão / login
  const [showConnect, setShowConnect] = useState(false);
  const [connectForm, setConnectForm] = useState({ provider: '', name: '', panel_url: '', username: '', password: '' });
  const [migrando, setMigrando] = useState(false);
  const [renaming, setRenaming] = useState(null); // { id, name }
  const [disconnectTarget, setDisconnectTarget] = useState(null);
  const [disconnecting, setDisconnecting] = useState(false);
  const [connecting, setConnecting] = useState(false);

  // Busca / importação
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState(null);
  const [catalogoPlanos, setCatalogoPlanos] = useState(null); // catálogo de planos (Dominus)
  const [selected, setSelected] = useState({});
  const [prices, setPrices] = useState({});
  const [months, setMonths] = useState({});
  const [connectionId, setConnectionId] = useState('');
  const [plans, setPlans] = useState([]);
  const [onlyMine, setOnlyMine] = useState(true);
  const [planDraft, setPlanDraft] = useState({}); // "pkgId|telas" -> { price, months }
  const [creatingPlans, setCreatingPlans] = useState(false);
  const [testPlanId, setTestPlanId] = useState('');
  const [dominusPlanId, setDominusPlanId] = useState('');

  const [importing, setImporting] = useState(false);

  useEffect(() => { fetchIntegrations(); }, []);

  const fetchPlans = async () => {
    try { const r = await api.get('/customer-plans'); setPlans(r.data || []); } catch { /* silencioso */ }
  };

  const fetchIntegrations = async () => {
    try {
      setLoading(true);
      const res = await api.get('/sigma/login/integrations');
      const list = res.data || [];
      setIntegrations(list);
      if (list.length > 0) setActiveId((prev) => prev || list[0].id);
    } catch (e) {
      // silencioso
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async () => {
    const _meta = providerMeta(connectForm.provider);
    const _isToken = _meta.token;
    const _url = _meta.fixedUrl || connectForm.panel_url;  // NATV usa URL fixa
    if (!_url || !connectForm.password || (!_isToken && !connectForm.username)) {
      toast.error(_isToken ? 'Preencha o token' : 'Preencha URL, usuário e senha do painel');
      return;
    }
    try {
      setConnecting(true);
      const _payload = { ...connectForm, panel_url: _url, username: connectForm.username || (_isToken ? connectForm.name : connectForm.username) };
      const res = await api.post('/sigma/login/connect', _payload);
      toast.success(`Integração conectada: ${connectForm.name.trim() || res.data.display_name} (${res.data.credits} créditos)`);
      setShowConnect(false);
      setConnectForm({ name: '', panel_url: '', username: '', password: '' });
      await fetchIntegrations();
      setActiveId(res.data.id);
      setConnectForm({ provider: '', name: '', panel_url: '', username: '', password: '' });
      onChanged && onChanged();
    } catch (e) {
      toast.error(errText(e, 'Falha ao conectar'));
    } finally {
      setConnecting(false);
    }
  };

  const saveRename = async () => {
    if (!renaming) return;
    try {
      await api.put(`/sigma/login/integrations/${renaming.id}`, { name: renaming.name });
      toast.success('Nome atualizado');
      setRenaming(null);
      await fetchIntegrations();
      onChanged && onChanged();
    } catch (e) {
      toast.error(errText(e, 'Erro ao renomear'));
    }
  };

  const handleDisconnect = (integ) => setDisconnectTarget(integ);

  const confirmDisconnect = async () => {
    const integ = disconnectTarget;
    if (!integ) return;
    try {
      setDisconnecting(true);
      await api.delete(`/sigma/login/integrations/${integ.id}`);
      toast.success('Painel desconectado');
      setResults(null);
      setDisconnectTarget(null);
      await fetchIntegrations();
      if (activeId === integ.id) setActiveId(null);
      onChanged && onChanged();
    } catch (e) {
      toast.error(errText(e, 'Erro ao desconectar'));
    } finally {
      setDisconnecting(false);
    }
  };

  const migrarPlanos = async () => {
    const integ = integrations.find((i) => i.id === activeId);
    if (!integ) return;
    if (!window.confirm(`Migrar os planos antigos (token) para "${integ.name || 'esta integração'}"? Eles serão vinculados a este painel Sigma.`)) return;
    setMigrando(true);
    try {
      const r = await api.post(`/sigma/login/${activeId}/migrar-planos-token`);
      toast.success(r.data.mensagem || 'Planos migrados!');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao migrar'); }
    finally { setMigrando(false); }
  };
  const handleSearch = async () => {
    if (!activeId) { toast.error('Selecione um painel'); return; }
    try {
      setSearching(true);
      setResults(null);
      let res;
      if (query.trim()) {
        res = await api.post(`/sigma/login/${activeId}/search-customers`, { query, page: 1, only_mine: onlyMine });
      } else {
        // listagem completa roda em segundo plano (painéis grandes passam do tempo limite do servidor)
        const start = await api.post(`/sigma/login/${activeId}/list-job`, { only_mine: onlyMine });
        let job = null;
        for (let i = 0; i < 150; i++) {
          await new Promise((ok) => setTimeout(ok, 3000));
          const st = await api.get(`/sigma/login/list-jobs/${start.data.job_id}`);
          if (st.data.status !== 'running') { job = st.data; break; }
        }
        if (!job) throw { response: { data: { detail: 'A listagem demorou demais. Tente novamente.' } } };
        if (job.status === 'error') throw { response: { data: { detail: job.error || 'Erro ao listar clientes do painel' } } };
        res = { data: job.result };
      }
      const customers = (res.data.customers || []).map((c, i) => ({
        ...c,
        _rowid: (c.panel_customer_id !== undefined && c.panel_customer_id !== null && String(c.panel_customer_id) !== 'None')
          ? String(c.panel_customer_id) : `row-${i}-${c.username || ''}`,
      }));
      res.data.customers = customers;
      setResults(res.data);
      fetchPlans();
      // Dominus: busca o catálogo de planos (a API não informa o pacote de cada cliente)
      if (activeProvider === 'dominus') {
        try {
          const cat = await api.get(`/sigma/login/${activeId}/panel-packages`);
          setCatalogoPlanos(cat.data.packages || []);
        } catch { setCatalogoPlanos([]); }
      } else {
        setCatalogoPlanos(null);
      }
      // pré-selecionar não importados
      const sel = {};
      const pr = {};
      const mo = {};
      customers.forEach((c) => {
        // listagem completa começa sem seleção (evita importar centenas sem querer)
        if (!c.already_imported && query.trim()) sel[c._rowid] = true;
        if (!pr[c.package_name]) { pr[c.package_name] = ''; mo[c.package_name] = '1'; }
      });
      setSelected(sel);
      setPrices((p) => ({ ...pr, ...p }));
      setMonths((m) => ({ ...mo, ...m }));
      if (customers.length === 0) toast.info('Nenhum cliente encontrado');
      if (res.data.owner_filter_available === false && onlyMine) {
        toast.warning('O painel não informou o dono dos clientes; mostrando todos.');
      }
    } catch (e) {
      const status = e.response?.status;
      toast.error(e.response?.data?.detail || (status === 504 || status === 502
        ? 'O painel demorou para responder. Tente de novo em instantes.'
        : `Erro ao buscar clientes${status ? ` (${status})` : ''}`));
    } finally {
      setSearching(false);
    }
  };

  const handleImport = async () => {
    const chosen = (results?.customers || []).filter((c) => selected[c._rowid] && !c.already_imported);
    if (chosen.length === 0) { toast.error('Selecione ao menos um cliente'); return; }
    if (chosen.some((c) => c.is_test) && !testPlanId) {
      toast.error('Selecione um plano para substituir os pacotes de teste');
      return;
    }
    if (usaPlanoEscolhido && !dominusPlanId) {
      toast.error('Escolha o plano que os clientes terão');
      return;
    }
    try {
      setImporting(true);
      const res = await api.post(`/sigma/login/${activeId}/import-customers`, {
        clients: chosen,
        connection_id: connectionId && connectionId !== 'none' ? connectionId : null,
        package_prices: prices,
        package_months: months,
        test_package_plan_id: testPlanId || null,
        default_plan_id: (usaPlanoEscolhido ? dominusPlanId : null),
      });
      toast.success(res.data.message);
      if (res.data.errors?.length) {
        res.data.errors.slice(0, 5).forEach((e) => toast.warning(e));
      }
      if ((res.data.billable || []).length > 0) setBillOffer({ customers: res.data.billable });
      const ids = new Set(chosen.map((c) => c._rowid));
      setResults((prev) => prev && { ...prev, customers: prev.customers.map((c) => (ids.has(c._rowid) ? { ...c, already_imported: true } : c)) });
      setSelected({});
    } catch (e) {
      toast.error(errText(e, 'Erro ao importar'));
    } finally {
      setImporting(false);
    }
  };

  const sendBillingToImported = async () => {
    const ids = (billOffer?.customers || []).map((c) => c.id);
    setBillOffer(null);
    if (!ids.length) return;
    try {
      await api.post('/customers/bulk-billing', { customer_ids: ids, delay_min: 3, delay_max: 6, kind: 'billing' });
      toast.success('Cobrança iniciada! Acompanhe pela bolinha no canto.');
      window.dispatchEvent(new CustomEvent('bulk-billing-started'));
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao iniciar cobrança'); }
  };

  // duração sugerida a partir do nome do pacote ("1/Mês", "3/Mês", "Anual", "Semestral"...)
  const guessMonths = (name) => {
    const n = (name || '').toLowerCase();
    const m = n.match(/(\d+)\s*\/?\s*(m[eê]s|meses)/);
    if (m) return String(Math.max(1, parseInt(m[1], 10)));
    if (/anual|\bano\b/.test(n)) return '12';
    if (/semestral/.test(n)) return '6';
    if (/trimestral/.test(n)) return '3';
    if (/bimestral/.test(n)) return '2';
    return '1';
  };
  const pkgKey = (c) => `${c.package_id}|${c.connections || 1}`;
  const activeProvider = integrations.find((i) => i.id === activeId)?.provider;
  const usaPlanoEscolhido = activeProvider === 'dominus' || activeProvider === 'natv';
  // pacotes reais (não teste) que aparecem na lista, com quantos clientes usam cada um
  const panelPackages = (activeProvider === 'dominus' && catalogoPlanos)
    ? catalogoPlanos.map((p) => ({ key: `${p.id}|${p.connections || 1}`, package_id: p.id, package_name: p.name, connections: p.connections || 1, has_plan: p.has_plan, duration_months: p.duration_months || (p.duration_days ? Math.max(1, Math.round(p.duration_days/30)) : 1), count: 0 }))
    : Object.values((results?.customers || []).filter((c) => !c.is_test && c.package_id).reduce((acc, c) => {
        const k = pkgKey(c);
        if (!acc[k]) acc[k] = { key: k, package_id: c.package_id, package_name: c.package_name, connections: c.connections || 1, has_plan: c.has_plan, count: 0 };
        acc[k].count += 1;
        acc[k].has_plan = acc[k].has_plan || c.has_plan;
        return acc;
      }, {})).sort((a, b) => b.count - a.count);
  const packagesWithoutPlan = panelPackages.filter((p) => !p.has_plan);

  const createPanelPlans = async () => {
    const pending = packagesWithoutPlan.filter((p) => (planDraft[p.key]?.price ?? '') !== '');
    if (!pending.length) { toast.error('Preencha o preço de pelo menos um pacote'); return; }
    try {
      setCreatingPlans(true);
      const r = await api.post(`/sigma/login/${activeId}/create-plans`, {
        packages: pending.map((p) => ({
          package_id: p.package_id, package_name: p.package_name, connections: p.connections,
          plan_name: (planDraft[p.key]?.name || p.package_name),
          price: parseFloat(planDraft[p.key].price), duration_months: parseInt(planDraft[p.key]?.months || p.duration_months || guessMonths(p.package_name), 10) || 1,
        })),
      });
      toast.success(r.data.message);
      (r.data.errors || []).slice(0, 5).forEach((m) => toast.warning(m));
      const done = new Set(pending.map((p) => p.key));
      setResults((prev) => prev && { ...prev, customers: prev.customers.map((c) => (done.has(pkgKey(c)) ? { ...c, has_plan: true } : c)) });
      await fetchPlans();
    } catch (e) {
      toast.error(errText(e, 'Erro ao criar planos'));
    } finally {
      setCreatingPlans(false);
    }
  };

  const selectedRows = (results?.customers || []).filter((c) => selected[c._rowid] && !c.already_imported);
  // Só pedem preço os pacotes normais que ainda não têm plano
  const newPackages = [...new Set(selectedRows.filter((c) => !c.is_test && !c.has_plan).map((c) => c.package_name))];
  const hasTestSelected = selectedRows.some((c) => c.is_test);
  const hasTestInResults = (results?.customers || []).some((c) => c.is_test && !c.already_imported);

  return (
    <Card data-testid="sigma-login-card">
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <div>
            <CardTitle>Minhas integrações</CardTitle>
            <CardDescription>Painéis conectados à sua conta. Selecione um para importar clientes, sincronizar e renovar.</CardDescription>
          </div>
          {integrations.length > 0 && (
            <Badge variant="outline" className="text-[11px] text-muted-foreground shrink-0">
              {integrations.length} conectado{integrations.length > 1 ? 's' : ''}
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        ) : (
          <div className="space-y-6">
            {/* Galeria de painéis conectados + card de conectar */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {integrations.map((integ) => {
                const meta = providerMeta(integ.provider);
                const selected = activeId === integ.id;
                return (
                  <button
                    key={integ.id}
                    type="button"
                    onClick={() => { setActiveId(integ.id); setResults(null); }}
                    data-testid={`sigma-login-integ-${integ.id}`}
                    className={`group relative text-left rounded-2xl border p-4 transition-all ${selected ? 'border-primary ring-1 ring-primary/40 bg-primary/[0.04]' : 'border-border/60 hover:border-border bg-card hover:shadow-sm'}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${meta.tint} flex items-center justify-center shrink-0`}>
                        <KeyRound className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex items-center gap-1">
                        <span
                          role="button" tabIndex={0} title="Renomear"
                          onClick={(e) => { e.stopPropagation(); setRenaming({ id: integ.id, name: integ.name || '' }); }}
                          className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/60"
                          data-testid={`sigma-login-rename-${integ.id}`}
                        ><Pencil className="w-3.5 h-3.5" /></span>
                        <span
                          role="button" tabIndex={0} title="Desconectar"
                          onClick={(e) => { e.stopPropagation(); handleDisconnect(integ); }}
                          className="p-1.5 rounded-md text-muted-foreground hover:text-red-500 hover:bg-red-500/10"
                          data-testid={`sigma-login-disconnect-${integ.id}`}
                        ><XCircle className="w-3.5 h-3.5" /></span>
                      </div>
                    </div>

                    {renaming?.id === integ.id ? (
                      <div className="mt-3 flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                        <Input autoFocus className="h-8" maxLength={40} value={renaming.name}
                          onChange={(e) => setRenaming({ ...renaming, name: e.target.value })}
                          onKeyDown={(e) => { if (e.key === 'Enter') saveRename(); if (e.key === 'Escape') setRenaming(null); }}
                          placeholder="Nome da integração" />
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-emerald-500" onClick={saveRename}><Check className="w-4 h-4" /></Button>
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => setRenaming(null)}><X className="w-4 h-4" /></Button>
                      </div>
                    ) : (
                      <div className="mt-3 min-w-0">
                        <p className="font-semibold truncate">{integ.name || integ.display_name}</p>
                        <p className="text-xs text-muted-foreground truncate">
                          {(integ.panel_url || '').replace(/^https?:\/\//, '')}
                        </p>
                      </div>
                    )}

                    <div className="mt-3 flex items-center gap-2 text-[11px]">
                      <span className="px-2 py-0.5 rounded-full bg-muted/70 text-muted-foreground">{meta.name}</span>
                      {integ.display_name && <span className="text-muted-foreground truncate">{integ.display_name}</span>}
                      {selected && <span className="ml-auto text-primary font-medium">Selecionado</span>}
                    </div>
                  </button>
                );
              })}

              {/* Card de conectar */}
              <button
                type="button"
                onClick={() => { setConnectForm({ provider: '', name: '', panel_url: '', username: '', password: '' }); setShowConnect(true); }}
                data-testid="sigma-login-connect-btn"
                className="rounded-2xl border border-dashed border-border/70 p-4 min-h-[132px] flex flex-col items-center justify-center gap-2 text-muted-foreground hover:text-foreground hover:border-primary/50 hover:bg-muted/30 transition-colors"
              >
                <div className="w-10 h-10 rounded-xl border border-dashed border-current flex items-center justify-center">
                  <Plus className="w-5 h-5" />
                </div>
                <span className="text-sm font-medium">Conectar painel</span>
              </button>
            </div>

            {/* Busca de clientes */}
            {activeId && (
              <div className="border rounded-lg p-4 space-y-4">
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-primary" />
                  <h3 className="font-semibold">Importar Clientes</h3>
                </div>
                <div className="flex gap-2">
                  <Input value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleSearch()} placeholder="Pesquisar por usuário (vazio = todos)" data-testid="sigma-login-search-input" />
                  <Button onClick={handleSearch} disabled={searching} data-testid="sigma-login-search-btn">
                    {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                  </Button>
                </div>

                <div className="flex flex-wrap items-center justify-between gap-2">
                  <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
                    <Switch checked={onlyMine} onCheckedChange={setOnlyMine} data-testid="sigma-only-mine" />
                    Só meus clientes (ocultar os das minhas revendas)
                  </label>
                  {searching && !query.trim() && (
                    <span className="text-xs text-muted-foreground">Lendo todas as páginas do painel… pode levar um tempinho.</span>
                  )}
                </div>

                {results && (
                  <p className="text-xs text-muted-foreground">
                    {results.total} cliente(s)
                    {results.hidden_other_resellers > 0 && ` · ${results.hidden_other_resellers} de revendas ocultos`}
                    {results.customers?.some((c) => !c.already_imported) && ` · ${Object.values(selected).filter(Boolean).length} selecionado(s)`}
                  </p>
                )}

                {results && results.customers?.length > 0 && (
                  <>
                    <div className="rounded-lg border overflow-x-auto max-h-[28rem] overflow-y-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-10">
                              <Checkbox
                                checked={results.customers.filter(c => !c.already_imported).every(c => selected[c._rowid]) && results.customers.some(c => !c.already_imported)}
                                onCheckedChange={(checked) => {
                                  const s = { ...selected };
                                  results.customers.forEach(c => { if (!c.already_imported) s[c._rowid] = !!checked; });
                                  setSelected(s);
                                }}
                              />
                            </TableHead>
                            <TableHead>Usuário</TableHead>
                            <TableHead>Pacote</TableHead>
                            <TableHead>Conexões</TableHead>
                            <TableHead>Vencimento</TableHead>
                            <TableHead>Status</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {results.customers.map((c) => (
                            <TableRow key={c._rowid} className={c.already_imported ? 'opacity-50' : ''}>
                              <TableCell>
                                <Checkbox
                                  disabled={c.already_imported}
                                  checked={!!selected[c._rowid]}
                                  onCheckedChange={(checked) => setSelected(prev => ({ ...prev, [c._rowid]: !!checked }))}
                                />
                              </TableCell>
                              <TableCell className="font-medium">
                                {c.username}
                                {c.whatsapp && <p className="text-[11px] text-muted-foreground font-normal">{c.whatsapp}</p>}
                              </TableCell>
                              <TableCell className="text-xs max-w-[240px]">
                                <div className="flex items-center gap-1.5">
                                  <span className="truncate">{c.package_name}</span>
                                  {c.is_test && <Badge className="text-[10px] shrink-0 bg-amber-500/20 text-amber-500 border-amber-500/30">Teste</Badge>}
                                </div>
                              </TableCell>
                              <TableCell>{c.connections}</TableCell>
                              <TableCell className="text-xs">{c.expiration_date}</TableCell>
                              <TableCell>
                                {c.already_imported
                                  ? <Badge variant="outline" className="text-[10px]">Importado</Badge>
                                  : <Badge className="text-[10px] bg-emerald-500/20 text-emerald-500 border-emerald-500/30">{c.status}</Badge>}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>

                    {/* Planos do painel: criar todos de uma vez */}
                    {panelPackages.length > 0 && (
                      <div className="p-3 rounded-lg border border-border/50 space-y-3" data-testid="sigma-panel-plans">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div>
                            <p className="text-sm font-medium">Planos do painel</p>
                            <p className="text-xs text-muted-foreground">
                              {packagesWithoutPlan.length > 0
                                ? `${packagesWithoutPlan.length} pacote(s) ainda sem plano no NexuZap. Defina preço e duração e crie todos — cada cliente importado já vem no plano certo.`
                                : 'Todos os pacotes desta lista já têm plano no NexuZap.'}
                            </p>
                          </div>
                          {packagesWithoutPlan.length > 0 && (
                            <Button size="sm" onClick={createPanelPlans} disabled={creatingPlans} data-testid="sigma-create-plans">
                              {creatingPlans ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                              Criar planos
                            </Button>
                          )}
                        </div>
                        <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                          {panelPackages.map((p) => (
                            <div key={p.key} className="rounded-lg border border-border/50 p-2">
                              <p className="text-[11px] text-muted-foreground mb-1">
                                Pacote no painel: <b className="text-foreground">{p.package_name}</b>{p.connections > 1 ? ` · ${p.connections} telas` : ''} · {p.count} cliente(s)
                              </p>
                              {p.has_plan ? (
                                <div className="text-xs text-emerald-500 flex items-center gap-1">
                                  <CheckCircle2 className="w-3.5 h-3.5" /> Plano já criado
                                </div>
                              ) : (
                                <div className="grid grid-cols-12 gap-2">
                                  <Input className="col-span-12 sm:col-span-6 h-8" placeholder="Nome do plano"
                                    value={planDraft[p.key]?.name ?? p.package_name}
                                    onChange={(e) => setPlanDraft((d) => ({ ...d, [p.key]: { ...d[p.key], name: e.target.value } }))} />
                                  <Input className="col-span-6 sm:col-span-3 h-8" type="number" min="0" step="0.01" placeholder="Preço R$"
                                    value={planDraft[p.key]?.price ?? ''}
                                    onChange={(e) => setPlanDraft((d) => ({ ...d, [p.key]: { ...d[p.key], price: e.target.value } }))} />
                                  <Input className="col-span-6 sm:col-span-3 h-8" type="number" min="1" step="1" placeholder="Meses"
                                    value={planDraft[p.key]?.months ?? (p.duration_months || guessMonths(p.package_name))}
                                    onChange={(e) => setPlanDraft((d) => ({ ...d, [p.key]: { ...d[p.key], months: e.target.value } }))} />
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Dominus: clientes não têm pacote no painel — escolher um plano existente */}
                    {usaPlanoEscolhido && (results?.customers || []).some((c) => !c.already_imported) && (
                      <div className={`p-3 rounded-lg border space-y-2 ${dominusPlanId ? 'bg-amber-500/10 border-amber-500/30' : 'border-border/50'}`}>
                        <div className="flex items-center gap-2">
                          <AlertCircle className="w-4 h-4 text-amber-500" />
                          <p className="text-sm font-medium text-amber-500">Escolha o plano dos clientes</p>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Este painel não informa o plano de cada cliente. Escolha um plano já criado para atribuir aos clientes importados — usado na cobrança e na renovação:
                        </p>
                        <Select value={dominusPlanId} onValueChange={setDominusPlanId}>
                          <SelectTrigger><SelectValue placeholder="Selecione o plano..." /></SelectTrigger>
                          <SelectContent>
                            {plans.filter((p) => p.active !== false && p.sigma_login_integration_id === activeId).map((p) => (
                              <SelectItem key={p.id} value={p.id}>
                                {p.name} - R$ {Number(p.price || 0).toFixed(2)} ({p.duration_months || 1} {(p.duration_months || 1) === 1 ? 'mês' : 'meses'})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {plans.filter((p) => p.sigma_login_integration_id === activeId).length === 0 && <p className="text-xs text-amber-500">Nenhum plano criado para esta integração ainda. Crie um em "Planos" primeiro (defina nome, preço e duração em meses).</p>}
                      </div>
                    )}

                    {/* Pacotes de teste: substituir por um plano existente */}
                    {hasTestInResults && (
                      <div className={`p-3 rounded-lg border space-y-2 ${hasTestSelected ? 'bg-amber-500/10 border-amber-500/30' : 'border-border/50'}`}>
                        <div className="flex items-center gap-2">
                          <AlertCircle className="w-4 h-4 text-amber-500" />
                          <p className="text-sm font-medium text-amber-500">Pacotes de teste detectados</p>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Clientes em teste não criam plano novo. Escolha o plano que eles terão no NexuZap (usado na cobrança e na renovação):
                        </p>
                        <Select value={testPlanId} onValueChange={setTestPlanId}>
                          <SelectTrigger data-testid="sigma-login-test-plan"><SelectValue placeholder="Selecione o plano para os testes..." /></SelectTrigger>
                          <SelectContent>
                            {plans.filter((p) => p.active !== false).map((p) => (
                              <SelectItem key={p.id} value={p.id}>
                                {p.name} - R$ {Number(p.price || 0).toFixed(2)} ({p.duration_months || 1} {(p.duration_months || 1) === 1 ? 'mês' : 'meses'})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {plans.length === 0 && <p className="text-xs text-amber-500">Crie os planos acima em "Planos do painel" e depois escolha um aqui.</p>}
                      </div>
                    )}

                    {/* Preços/meses de novos pacotes */}
                    {newPackages.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-xs text-muted-foreground">Defina preço e duração (meses) para os planos novos:</p>
                        {newPackages.map((pkg) => (
                          <div key={pkg} className="grid grid-cols-12 gap-2 items-center">
                            <span className="col-span-6 text-xs truncate">{pkg}</span>
                            <Input className="col-span-3" type="number" step="0.01" placeholder="Preço R$" value={prices[pkg] || ''} onChange={(e) => setPrices(p => ({ ...p, [pkg]: e.target.value }))} />
                            <Input className="col-span-3" type="number" placeholder="Meses" value={months[pkg] || '1'} onChange={(e) => setMonths(m => ({ ...m, [pkg]: e.target.value }))} />
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-end">
                      <div>
                        <Label>Conexão WhatsApp <span className="text-muted-foreground font-normal">(opcional)</span></Label>
                        <Select value={connectionId || 'none'} onValueChange={setConnectionId}>
                          <SelectTrigger data-testid="sigma-login-connection-select"><SelectValue placeholder="Sem conexão" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Sem conexão WhatsApp</SelectItem>
                            {(connections || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <Button onClick={handleImport} disabled={importing} data-testid="sigma-login-import-btn">
                        {importing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                        Importar Selecionados
                      </Button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </CardContent>

      {/* Desconectar painel — confirmação */}
      <Dialog open={!!disconnectTarget} onOpenChange={(o) => { if (!o && !disconnecting) setDisconnectTarget(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Desconectar {disconnectTarget?.name || disconnectTarget?.display_name}?</DialogTitle>
            <DialogDescription>
              {disconnectTarget?.customers_count > 0 ? (
                <>
                  <b>{disconnectTarget.customers_count}</b> cliente{disconnectTarget.customers_count > 1 ? 's' : ''} {disconnectTarget.customers_count > 1 ? 'ficarão desvinculados' : 'ficará desvinculado'} deste painel.
                  {' '}Eles continuam no seu Gestor de Clientes, mas deixam de renovar e sincronizar automaticamente no painel até você reconectar.
                </>
              ) : (
                'Este painel ainda não tem clientes vinculados. Nada será perdido no seu Gestor de Clientes.'
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setDisconnectTarget(null)} disabled={disconnecting}>Cancelar</Button>
            <Button variant="destructive" onClick={confirmDisconnect} disabled={disconnecting} data-testid="confirm-disconnect">
              {disconnecting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Desconectar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Conectar painel — passo 1: provedor, passo 2: credenciais */}
      <Dialog open={showConnect} onOpenChange={(o) => { if (!o) { setShowConnect(false); } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{connectForm.provider ? `Conectar ${providerMeta(connectForm.provider).name}` : 'Conectar um painel'}</DialogTitle>
            <DialogDescription>
              {connectForm.provider
                ? 'Informe o endereço e o seu acesso de revendedor. Fazemos o login com segurança pelo servidor.'
                : 'De qual painel são os seus clientes?'}
            </DialogDescription>
          </DialogHeader>

          {!connectForm.provider ? (
            <div className="grid grid-cols-1 gap-2">
              {PROVIDERS.map((pv) => (
                <button
                  key={pv.id}
                  type="button"
                  onClick={() => setConnectForm({ ...connectForm, provider: pv.id, panel_url: '' })}
                  className="flex items-center gap-3 p-3 rounded-xl border border-border/60 hover:border-primary/50 hover:bg-muted/40 transition-colors text-left"
                  data-testid={`provider-${pv.id}`}
                >
                  <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${pv.tint} flex items-center justify-center shrink-0`}>
                    <KeyRound className="w-5 h-5 text-white" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium">{pv.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{pv.hint}</p>
                  </div>
                  <ArrowLeft className="w-4 h-4 ml-auto text-muted-foreground rotate-180" />
                </button>
              ))}
            </div>
          ) : (
            <div className="space-y-3">
              <button type="button" onClick={() => setConnectForm({ ...connectForm, provider: '' })}
                className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1">
                <ArrowLeft className="w-3 h-3" /> Trocar painel
              </button>
              <div>
                <Label>Nome da integração <span className="text-muted-foreground font-normal">(opcional)</span></Label>
                <Input value={connectForm.name} maxLength={40} onChange={(e) => setConnectForm({ ...connectForm, name: e.target.value })}
                  placeholder="Ex.: Painel Village, Servidor 2" data-testid="sigma-login-name-input" />
              </div>
              {!providerMeta(connectForm.provider).fixedUrl && (
                <div>
                  <Label>Endereço do painel</Label>
                  <Input value={connectForm.panel_url} onChange={(e) => setConnectForm({ ...connectForm, panel_url: e.target.value })}
                    placeholder={providerMeta(connectForm.provider).example || 'https://seupainel.com'} data-testid="sigma-login-url-input" />
                </div>
              )}
              {providerMeta(connectForm.provider).token ? (
                <div>
                  <Label>Token de API</Label>
                  <Input type="password" value={connectForm.password} onChange={(e) => setConnectForm({ ...connectForm, password: e.target.value })}
                    placeholder="dp_..." data-testid="dominus-token-input" />
                  <p className="text-[11px] text-muted-foreground mt-1">Gere em: Perfil → API de Integração no painel Dominus. Cole o token dp_... aqui.</p>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label>Usuário</Label>
                      <Input value={connectForm.username} onChange={(e) => setConnectForm({ ...connectForm, username: e.target.value })}
                        placeholder="seu_usuario" data-testid="sigma-login-user-input" />
                    </div>
                    <div>
                      <Label>Senha</Label>
                      <Input type="password" value={connectForm.password} onChange={(e) => setConnectForm({ ...connectForm, password: e.target.value })}
                        placeholder="••••••••" data-testid="sigma-login-pass-input" />
                    </div>
                  </div>
                  <div className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-[11px] text-amber-600 dark:text-amber-400">
                    <span className="text-sm shrink-0">⚠️</span>
                    <span><b>Importante:</b> a verificação em duas etapas (2FA) precisa estar <b>DESATIVADA</b> na conta do painel (Sigma, PainelFoda, Gerador Pro). Com o 2FA ligado, não conseguimos conectar.</span>
                  </div>
                  <div className="flex items-start gap-2 text-[11px] text-muted-foreground">
                    <Shield className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                    <span>Fazemos login por um navegador seguro no servidor. A primeira conexão pode levar alguns segundos.</span>
                  </div>
                </>
              )}
              <Button className="w-full" onClick={handleConnect} disabled={connecting} data-testid="sigma-login-submit-btn">
                {connecting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <KeyRound className="w-4 h-4 mr-2" />}
                Conectar
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Oferta de cobrança pós-importação */}
      <Dialog open={!!billOffer} onOpenChange={(o) => { if (!o) setBillOffer(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>⚡ Enviar cobrança?</DialogTitle>
            <DialogDescription>
              {(() => {
                const cs = billOffer?.customers || [];
                const venc = cs.filter((c) => c.days_left < 0).length;
                const veng = cs.length - venc;
                const partes = [];
                if (venc) partes.push(`${venc} vencido(s)`);
                if (veng) partes.push(`${veng} vencendo em breve`);
                return `Você importou ${partes.join(' e ')}. Enviar a mensagem de cobrança para eles agora?`;
              })()}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" onClick={() => setBillOffer(null)}>Agora não</Button>
            <Button onClick={sendBillingToImported}>Sim, enviar</Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
